/**
 * Client-side error capture with deduplication
 * Reports errors to the server for persistence via tail worker
 */

const reported = new Set();
const MAX_TRACKED_ERRORS = 100;

/**
 * Report an error to the logging endpoint
 * @param {Error|string} error - The error to report
 * @param {Object} context - Additional context to include
 */
export function reportError(error, context = {}) {
  if (typeof window === "undefined") return;

  const errorObj = error instanceof Error ? error : new Error(String(error));
  const key = `${errorObj.message}:${(errorObj.stack || "").slice(0, 100)}`;

  // Deduplicate errors
  if (reported.has(key)) return;
  if (reported.size >= MAX_TRACKED_ERRORS) {
    const firstKey = reported.values().next().value;
    if (firstKey) reported.delete(firstKey);
  }
  reported.add(key);

  fetch("/api/logs/client", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message: errorObj.message,
      stack: errorObj.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...context,
    }),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Initialize global error handlers
 * Call this once at app startup
 */
export function initErrorHandlers() {
  if (typeof window === "undefined") return;
  if (window.__errorHandlersInit) return;
  window.__errorHandlersInit = true;

  window.onerror = (message, source, lineno, colno, error) => {
    reportError(error || new Error(String(message)), {
      type: "onerror",
      source,
      lineno,
      colno,
    });
    return false;
  };

  window.onunhandledrejection = (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));
    reportError(error, { type: "unhandledrejection" });
  };

  console.log("[client-logger] Error handlers initialized");
}
